import React, { useState, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Input } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ModalBody } from 'ui-components/components/modal/modal-body';

import style from '../export-records-modal.module.less';
import { messages } from '../messages/general-export-messages';
import { ArrowButtons } from './arrow-buttons';
import { AvailableFieldList } from './available-field-list';
import { CreateOrEditFormatFooter } from './create-or-edit-format-footer';
import { SelectedFieldList } from './selected-field-list';

const validateForm = (selectedFields, formatName) => {
  return {
    noFieldsAreSelected: selectedFields.length === 0,
    nameHasNoLength: formatName.length === 0,
  };
};

const sanitizeFields = (selectedFields) => {
  return selectedFields.map((f, index) => {
    const modifiedField = { ...f };
    delete modifiedField.wasClicked;
    delete modifiedField.isHidden;
    modifiedField.sortOrder = index;
    return modifiedField;
  });
};

const CreateOrEditExportFormatModalView = ({
  formatName = '',
  formatId = '',
  startingAvailableFields,
  startingSelectedFields,
  onGoBack,
  saveExportFormat,
  intl,
  isLoading,
}) => {
  const [formErrors, setFormErrors] = useState({
    formatNameHasError: false,
    selectedFieldsHaveError: false,
  });
  const [availableFields, setAvailableFields] = useState(startingAvailableFields);
  const [selectedFields, setSelectedFields] = useState(startingSelectedFields);

  useEffect(() => {
    setAvailableFields(startingAvailableFields);
    setSelectedFields(startingSelectedFields);
  }, [startingAvailableFields, startingSelectedFields]);

  const editFieldName = useCallback(
    (fieldId, newFieldName) => {
      const newSelectedFields = selectedFields.map((f) => {
        if (f.fieldId === fieldId) {
          return {
            ...f,
            name: newFieldName,
          };
        }
        return f;
      });
      setSelectedFields(newSelectedFields);
    },
    [selectedFields],
  );

  const moveFieldsToSelected = useCallback(() => {
    const filteredAvailableFields = availableFields.map((f) => {
      if (f.wasClicked) {
        return {
          ...f,
          isHidden: true,
          wasClicked: false,
        };
      }
      return f;
    });
    setAvailableFields(filteredAvailableFields);

    const nowSelectedFields = availableFields.filter((f) => f.wasClicked);
    setSelectedFields([...selectedFields, ...nowSelectedFields]);
    if (selectedFields.length || nowSelectedFields.length) {
      setFormErrors({
        ...formErrors,
        selectedFieldsHaveError: false,
      });
    }
  }, [availableFields, selectedFields, formErrors]);

  const moveFieldsToAvailable = useCallback(() => {
    const filteredCurrentFields = selectedFields.filter((f) => !f.wasClicked);
    setSelectedFields(filteredCurrentFields);

    const currentSelectedFieldIds = filteredCurrentFields.map((f) => f.fieldId);
    const nowAvailableFields = availableFields.map((f) => {
      if (currentSelectedFieldIds.includes(f.fieldId)) {
        return f;
      }
      if (selectedFields.find((sf) => sf.fieldId === f.fieldId)) {
        return {
          ...f,
          isHidden: false,
          wasClicked: true,
        };
      }
      return {
        ...f,
        isHidden: false,
      };
    });
    setAvailableFields(nowAvailableFields);
  }, [selectedFields, availableFields]);

  const [name, setName] = useState(formatName);
  const onFormatNameChange = useCallback(
    (value) => {
      if (value.length > 0) {
        setFormErrors({
          ...formErrors,
          formatNameHasError: false,
        });
      }
      setName(value);
    },
    [formErrors],
  );

  const onSaveButtonClicked = useCallback(() => {
    const { noFieldsAreSelected, nameHasNoLength } = validateForm(selectedFields, name);
    if (noFieldsAreSelected || nameHasNoLength) {
      return setFormErrors({
        formatNameHasError: nameHasNoLength,
        selectedFieldsHaveError: noFieldsAreSelected,
      });
    }
    saveExportFormat({
      formatId,
      name,
      includedFields: sanitizeFields(selectedFields),
    });
  }, [saveExportFormat, formatId, name, selectedFields]);

  return (
    <>
      <ModalBody>
        <div className={style['export-records-modal-body']}>
          <div className={style['update-format-header']}>
            <FormattedMessage {...messages.updateFormatHeader} />
          </div>
          <div className={style['update-format-name']}>
            <Input
              className={style['input-width']}
              hasError={formErrors.formatNameHasError}
              onChange={onFormatNameChange}
              placeholder={intl.formatMessage(messages.updateFormatNamePlaceholder)}
              value={name}
            />
            {formErrors.formatNameHasError && (
              <div className={style['form-error-message']}>
                <FormattedMessage {...messages.missingFormatName} />
              </div>
            )}
          </div>
          <div className={style['update-fields-label']}>
            <FormattedMessage {...messages.updateFormatFieldLabel} />
          </div>
          {isLoading ? (
            <LoadingIndicator className={style['loading-indicator']} theme='grey' />
          ) : (
            <div className={style['update-fields']}>
              <AvailableFieldList availableFields={availableFields} setAvailableFields={setAvailableFields} />
              <div className={style['field-buttons']}>
                <ArrowButtons onLeftArrowClicked={moveFieldsToAvailable} onRightArrowClicked={moveFieldsToSelected} />
              </div>
              <SelectedFieldList
                editFieldName={editFieldName}
                selectedFields={selectedFields}
                selectedFieldsHaveError={formErrors.selectedFieldsHaveError}
                setSelectedFields={setSelectedFields}
              />
            </div>
          )}
        </div>
      </ModalBody>
      <CreateOrEditFormatFooter onGoBack={onGoBack} onSave={onSaveButtonClicked} />
    </>
  );
};

CreateOrEditExportFormatModalView.propTypes = {
  startingAvailableFields: PropTypes.array.isRequired,
  startingSelectedFields: PropTypes.array.isRequired,
  onGoBack: PropTypes.func.isRequired,
  formatName: PropTypes.string,
  formatId: PropTypes.string,
  saveExportFormat: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export const CreateOrEditFormat = injectIntl(CreateOrEditExportFormatModalView);
